import { BooleanInput, NumberInput, coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import {
    Component,
    ChangeDetectionStrategy,
    ViewChild,
    ElementRef,
    OnChanges,
    SimpleChanges,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { LineGraphDrawer, ScaleType } from './line-graph-drawer';

@Component({
    selector: 'se-line-graph',
    templateUrl: './line-graph.component.html',
    styleUrls: ['./line-graph.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineGraphComponent implements OnDestroy, OnChanges, OnInit {
    @ViewChild('graph', { static: true })
    graph!: ElementRef<SVGElement>;

    @Input()
    width: NumberInput;

    @Input()
    height: NumberInput;

    @Input()
    labelXRotation: NumberInput = 0;

    @Input()
    labelYRotation: NumberInput = 0;

    @Input()
    marginX: NumberInput = 25;

    @Input()
    marginY: NumberInput = 25;

    @Input()
    paddingX: NumberInput = 0;

    @Input()
    paddingY: NumberInput = 0;

    @Input()
    lines: any[][] = [];

    @Input()
    propX: string = 'x';

    @Input()
    propY: string = 'y';

    @Input()
    scaleX: ScaleType = 'linear';

    @Input()
    scaleY: ScaleType = 'linear';

    @Input()
    ticksX: NumberInput = undefined;

    @Input()
    ticksY: NumberInput = undefined;

    @Input()
    debug: BooleanInput = false;

    @Input()
    completeRange: BooleanInput = false;

    private drawer = new LineGraphDrawer();

    private resizeObserver!: ResizeObserver;
    private currentWidth!: number;
    private currentHeight!: number;
    private observeWidth = true;
    private observeHeight = true;

    constructor(private hostElement: ElementRef) {}

    ngOnInit() {
        this.observeWidth = this.width === undefined;
        this.observeHeight = this.height === undefined;
        this.resizeObserver = new ResizeObserver((entries) => this.onResize(entries));
        this.resizeObserver.observe(this.hostElement.nativeElement);

        this.currentWidth = coerceNumberProperty(this.width) ?? 300;
        this.currentHeight = coerceNumberProperty(this.height) ?? 300;
    }

    ngOnDestroy() {
        this.resizeObserver.disconnect();
    }

    ngOnChanges({ lines }: SimpleChanges) {
        if (lines && this.currentWidth && this.currentHeight) {
            this.onLinesChange();
        }
    }

    private onLinesChange() {
        this.lines = this.lines ?? [];

        this.drawer.draw(this.graph.nativeElement, {
            completeRange: coerceBooleanProperty(this.completeRange),
            debug: coerceBooleanProperty(this.debug),
            height: this.currentHeight,
            labelXRotation: coerceNumberProperty(this.labelXRotation),
            labelYRotation: coerceNumberProperty(this.labelYRotation),
            lines: this.lines,
            marginX: coerceNumberProperty(this.marginX),
            marginY: coerceNumberProperty(this.marginY),
            paddingX: coerceNumberProperty(this.paddingX),
            paddingY: coerceNumberProperty(this.paddingY),
            propX: this.propX,
            propY: this.propY,
            scaleX: this.scaleX,
            scaleY: this.scaleY,
            ticksX: coerceNumberProperty(this.ticksX),
            ticksY: coerceNumberProperty(this.ticksY),
            width: this.currentWidth,
        });
    }

    private onResize(entries: ResizeObserverEntry[]) {
        let targetWidth = undefined;
        let targetHeight = undefined;

        // Per ogni entry guarda le nuove dimensioni.
        for (const entry of entries) {
            if (entry.contentRect) {
                // Assegna le nuove larghezze / altezze solo se non sono impostate come fisse dall'esterno.
                targetWidth = this.observeWidth ? entry.contentRect.width : undefined;
                targetHeight = this.observeHeight ? entry.contentRect.height : undefined;
            }
        }

        if ((targetWidth && targetWidth < 250) || (targetHeight && targetHeight < 100)) {
            this.drawer.clear(this.graph.nativeElement);
            return;
        }

        if (targetWidth !== undefined || targetHeight !== undefined) {
            this.currentWidth = targetWidth ?? this.currentWidth;
            this.currentHeight = targetHeight ?? this.currentHeight;
            this.onLinesChange();
        }
    }
}
